import type { Row } from '@tanstack/react-table';
import _ from 'lodash';
import type { MouseEvent } from 'react';
import { useEffect, useMemo } from 'react';

import { typography } from '@adflow/frontend/ui-theme';
import { scrollIntoView, useRefMap } from '@adflow/frontend/util-common';

import { DataTableRow } from './DataTableRow';
import { dataTableStyles } from './dataTableStyles';

const styles = dataTableStyles;

export interface DataTableRowsProps<T> {
	rows: Row<T>[];

	sidebarStyle?: boolean;
	focusedKeyAndValue?: {
		key: keyof T;
		value: string | number;
		archived?: boolean;
	};
	onRowClick?: (row: T, e?: MouseEvent<HTMLTableRowElement>) => void;

	isHeaderFooterRendered?: boolean;

	showSeparationLines?: boolean;
	onlySticky?: boolean;
}

export function DataTableRows<T>({
	rows,
	sidebarStyle,
	showSeparationLines,
	onlySticky,
	onRowClick,
	focusedKeyAndValue,
	isHeaderFooterRendered = true,
}: DataTableRowsProps<T>) {
	const { key, value, archived } = focusedKeyAndValue ?? {};

	const rowRefs = useRefMap<Row<T>['id'], HTMLTableRowElement>();

	const selectedRowId = useMemo(() => {
		if (!key || !value) return null;

		const row = rows.find((r) => r.original?.[key] === value);
		return row?.id ?? null;
	}, [key, rows, value]);

	const allRefsPlaced = rows.every((r) => !!rowRefs(r.id).current);

	useEffect(() => {
		if (!allRefsPlaced || !isHeaderFooterRendered) return;

		// Scroll to selected row, or first row if none selected
		const selectedRow =
			rows.find((r) => r.id === selectedRowId) ?? _.first(rows);
		if (!selectedRow) return;

		const selectedRowEl = rowRefs(selectedRow.id).current;

		scrollIntoView(selectedRowEl, {
			block: 'start',
			inline: 'nearest',
			behavior: 'instant',
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [allRefsPlaced, isHeaderFooterRendered, rowRefs, rows]);

	return (
		<>
			{rows.map((row, index) => {
				const isRowSelected = !!sidebarStyle && selectedRowId === row.id;
				return (
					<DataTableRow
						key={row.id}
						row={row}
						rowRef={rowRefs(row.id)}
						onRowClick={onRowClick}
						rowStyle={[
							styles.tableRow,
							sidebarStyle ? styles.sidebarRowStyle : undefined,
							isRowSelected
								? [
										styles.focusedTableRow,
										archived ? styles.focusedTableRowArchived : undefined,
									]
								: undefined,
							{ position: 'relative' },
						]}
						cellStyle={[
							styles.tableCell,
							sidebarStyle ? typography.regular : undefined,
							sidebarStyle && showSeparationLines
								? {
										...(index === 0 && { borderTopWidth: 1 }),
										borderBottomWidth: 1,
									}
								: undefined,
						]}
						showTopEdgeShadow={isRowSelected ? index !== 0 : false}
						showBottomEdgeShadow={isRowSelected}
						onlySticky={onlySticky}
					/>
				);
			})}
		</>
	);
}
